package com.arena_web.ui.sections.profile

import androidx.compose.runtime.*
import com.arena_web.core.viewmodel.MATCH_HISTORY_PAGE_SIZE
import com.arena_web.data.dto.EventDto
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.icons.TintedGlyph
import com.arena_web.ui.components.widgets.LoadingSpinner
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.arena_web.navigation.Routes
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Img

/** Convert a server-reported total event count into the page count used by
 *  the `RecentMatchesSection` arrow navigation. Divides by the shared page
 *  size and rounds up. Exposed so call-sites (`Profile.kt`, `profile/Id.kt`,
 *  mobile content) compute the same value the VM paginates against. */
fun matchHistoryTotalPages(total: Long): Int =
    ((total + MATCH_HISTORY_PAGE_SIZE - 1) / MATCH_HISTORY_PAGE_SIZE).toInt()

@Composable
fun RecentMatchesSection(
    matches: List<EventDto>,
    currentPage: Int,
    totalPages: Int,
    onPageChange: (Int) -> Unit,
    isLoading: Boolean = false,
    compact: Boolean = false,
    modifier: Modifier = Modifier
) {
    val t = LocalAppStrings.current

    // Per Figma — bg & border both use "Surface/inner/gradient_L"
    // (#003BAE33 → #0357E633 with 15% layer opacity → alphas 0.03 / 0.03).
    val surfaceInnerL =
        "linear-gradient(-185deg, rgba(60, 121, 241, 0.165) 32%, rgba(5, 4, 12, 0.03) 75%)"

    // Outer wrapper with backdrop blur + gradient
    Column(
        modifier = modifier
            .fillMaxWidth()
            .then(if (compact) Modifier else Modifier.sBorderRadius(40))
            .overflow(Overflow.Hidden)
            .styleModifier {
                if (!compact) {
                    property("backdrop-filter", "blur(${s(11.165)})")
                    property("-webkit-backdrop-filter", "blur(${s(21.165)})")
                    property("background", surfaceInnerL)
                    property("position", "relative")
                }
            }
    ) {
        if (!compact) {
            // Gradient border ring — same Surface/inner/gradient_L, mask-composite trick
            // so it follows the radius-40 corners.
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .styleModifier {
                        property("position", "absolute")
                        property("inset", "0")
                        property("border", "1px solid transparent")
                        property("border-radius", s(40))

                        property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                        property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                        property("-webkit-mask-composite", "xor")
                        property("mask-composite", "exclude")
                        property("pointer-events", "none")
                    }
            )
        }
    // Inner card
    Column(
        modifier = Modifier
            .fillMaxSize()
            .then(if (compact) Modifier.borderRadius(16.px).padding(16.px).gap(12.px)
                  else Modifier.sHeight(390).sBorderRadius(40).sPadding(16, 16).sPaddingBottom(18).sGap(24).overflow(Overflow.Hidden))
            .styleModifier {
                property("border", "1px solid rgba(41, 52, 138, 0.2)")
                property(
                    "background",
                    "linear-gradient(14deg, rgba(0, 59, 174, 0.03) 26.9%, rgba(3, 87, 230, 0.03) 67.5%)"
                )
            }
    ) {
        // Header: title + nav arrows
        Row(
            modifier = Modifier.fillMaxWidth().then(if (compact) Modifier else Modifier.sPaddingLeft(8)),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                t.profileRecentMatchesTitle,
                modifier = Modifier
                    .then(if (compact) Modifier.fontSize(16.px) else Modifier.sFontSize(24))
                    .fontWeight(AppTypography.Medium)
                    .then(if (compact) Modifier else Modifier.sLetterSpacing(0.24).sLineHeight(32))
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .flexGrow(1)
            )
            if (totalPages >= 1) {
                Row(
                    modifier = Modifier.then(if (compact) Modifier.gap(6.px) else Modifier.sGap(8)),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    NavArrow(
                        enabled = currentPage > 0,
                        direction = "left",
                        compact = compact,
                        onClick = { if (currentPage > 0) onPageChange(currentPage - 1) }
                    )
                    SpanText(
                        "${currentPage + 1}/$totalPages",
                        modifier = Modifier
                            .then(if (compact) Modifier.fontSize(12.px) else Modifier.sFontSize(14))
                            .fontWeight(AppTypography.Regular)
                            .color(com.arena_web.ui.theme.ThemeVars.Text3)
                    )
                    NavArrow(
                        enabled = currentPage < totalPages - 1,
                        direction = "right",
                        compact = compact,
                        onClick = { if (currentPage < totalPages - 1) onPageChange(currentPage + 1) }
                    )
                }
            }
        }

        // Match list. Loader wins over empty-state — the section starts out
        // empty before the first network response, so without this branch
        // the user would see "No events scheduled yet" flicker for the
        // duration of the request even when there's data on the way.
        if (isLoading) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .then(if (compact) Modifier.height(120.px) else Modifier.flexGrow(1)),
                contentAlignment = Alignment.Center
            ) {
                // Intentionally empty — no loading indicator (user pref).
            }
        } else if (matches.isEmpty()) {
            if (compact) {
                Box(
                    modifier = Modifier.fillMaxWidth().height(80.px),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        t.profileRecentMatchesEmptyTitle,
                        modifier = Modifier.bodySRegular().color(com.arena_web.ui.theme.ThemeVars.Text3)
                    )
                }
            } else {
                Box(
                    modifier = Modifier.fillMaxWidth().flexGrow(1),
                    contentAlignment = Alignment.TopCenter
                ) {
                    NoEventsEmptyState()
                }
            }
        } else {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .flexGrow(1)
                    .then(if (compact) Modifier.gap(8.px) else Modifier.sGap(8))
                    .styleModifier { property("justify-content", "flex-start") }
            ) {
                matches.forEach { match ->
                    MatchRow(match, compact)
                }
            }
        }
    } // inner card
    } // outer wrapper
}

@Composable
private fun NavArrow(
    enabled: Boolean,
    direction: String,
    compact: Boolean,
    onClick: () -> Unit
) {
    Box(
        modifier = Modifier
            .then(if (compact) Modifier.size(32.px).borderRadius(100.px) else Modifier.sSize(36).sBorderRadius(100))
            .cursor(if (enabled) Cursor.Pointer else Cursor.Default)
            .styleModifier {
                property("border", "0.3px solid ${if (enabled) "var(--arena-border-top)" else "var(--arena-border-bottom)"}")
                property("background", AppGradients.InteractiveSecondary)
                property("opacity", if (enabled) "1" else "0.4")
                property("transition", "opacity 0.2s")
            }
            .onClick { onClick() },
        contentAlignment = Alignment.Center
    ) {
        Icon(
            src = AppIcons.ChevronForward,
            modifier = Modifier
                .styleModifier { property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V) }
                .then(if (compact) Modifier.size(12.px) else Modifier.sSize(14))
                .then(if (direction == "left") Modifier.styleModifier { property("transform", "rotate(180deg)") } else Modifier)
        )
    }
}

@Composable
private fun MatchRow(match: EventDto, compact: Boolean = false) {
    val t = LocalAppStrings.current
    val ctx = rememberPageContext()
    val resultShadow = if (compact) {
        when (match.result?.uppercase()) {
            "WIN" -> "inset -1px 1px 20px rgba(10,54,247,0.2)"
            "DRAW" -> "inset -1px 1px 20px rgba(0,255,59,0.15)"
            "LOSS" -> "inset -1px 1px 20px rgba(255,47,0,0.2)"
            else -> "none"
        }
    } else {
        when (match.result?.uppercase()) {
            "WIN" -> "inset ${s(-0.657)} ${s(0.657)} ${s(33)} rgba(10,54,247,0.2), " +
                    "inset ${s(-0.657)} ${s(0.657)} ${s(33)} rgba(10,54,247,0.2)"
            "DRAW" -> "inset ${s(-0.657)} ${s(0.657)} ${s(33)} rgba(0,255,59,0.15), " +
                    "inset ${s(-0.657)} ${s(0.657)} ${s(33)} rgba(0,255,59,0.15)"
            "LOSS" -> "inset ${s(-0.657)} ${s(0.657)} ${s(33)} rgba(255,47,0,0.2), " +
                    "inset ${s(-0.657)} ${s(0.657)} ${s(33)} rgba(255,47,0,0.2)"
            else -> "none"
        }
    }

    val teamA = match.teamScores.getOrNull(0)
    val teamB = match.teamScores.getOrNull(1)

    if (compact) {
        val date = formatShortDate(match.dateTime)
        val locationLabel = match.city?.takeIf { it.isNotBlank() } ?: match.address
        val sportLabel = (match.customSportName ?: match.sportName).takeIf { it.isNotBlank() }
        // Mobile: compact card — title row + score row + meta row
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .borderRadius(10.px)
                .padding(10.px, 12.px)
                .backgroundColor(AppColors.BackgroundMatchBase)
                .gap(6.px)
                .cursor(Cursor.Pointer)
                .onClick { ctx.router.navigateTo("${Routes.EVENTS}/${match.id}") }
                .styleModifier { property("box-shadow", resultShadow) }
        ) {
            // Header: title + optional sport / Pro tag
            Row(
                modifier = Modifier.fillMaxWidth().gap(6.px),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    match.title,
                    modifier = Modifier.fontSize(13.px).fontWeight(AppTypography.Medium)
                        .color(AppColors.Light.Background).flexGrow(1)
                        .styleModifier {
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                            property("white-space", "nowrap")
                        }
                )
                if (sportLabel != null) {
                    SpanText(
                        if (match.isPro) t.profileRecentMatchesSportPro(sportLabel) else sportLabel,
                        modifier = Modifier.fontSize(11.px).fontWeight(AppTypography.Medium)
                            .color(com.arena_web.ui.theme.ThemeVars.Text3)
                    )
                }
            }
            // Score row
            Row(
                modifier = Modifier.fillMaxWidth().gap(8.px),
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Team A: logo + name
                Row(modifier = Modifier.flexGrow(1).gap(6.px), verticalAlignment = Alignment.CenterVertically) {
                    if (teamA?.imageUrl != null) {
                        Img(
                            src = teamA.imageUrl!!,
                            alt = teamA.name,
                            attrs = Modifier.size(24.px).borderRadius(12.px).toAttrs()
                        )
                    } else {
                        Box(
                            modifier = Modifier.size(24.px).borderRadius(12.px)
                                .backgroundColor(Color(teamA?.color ?: "var(--l-s3, #03051F)")),
                            contentAlignment = Alignment.Center
                        ) {
                            Icon(src = AppIcons.TeamPeople, modifier = Modifier.size(14.px))
                        }
                    }
                    SpanText(
                        teamA?.name ?: t.profileRecentMatchesTeamA,
                        modifier = Modifier.fontSize(13.px).fontWeight(500)
                            .color(com.arena_web.ui.theme.ThemeVars.Text1)
                    )
                }
                // Score
                Row(modifier = Modifier.gap(4.px), verticalAlignment = Alignment.CenterVertically) {
                    SpanText(
                        "${teamA?.score ?: 0}",
                        modifier = Modifier.fontSize(18.px).fontWeight(AppTypography.SemiBold)
                            .color(com.arena_web.ui.theme.ThemeVars.Text1)
                    )
                    SpanText(":", modifier = Modifier.fontSize(13.px).color(com.arena_web.ui.theme.ThemeVars.Text3))
                    SpanText(
                        "${teamB?.score ?: 0}",
                        modifier = Modifier.fontSize(18.px).fontWeight(AppTypography.SemiBold)
                            .color(com.arena_web.ui.theme.ThemeVars.Text1)
                    )
                }
                // Team B: name + logo
                Row(
                    modifier = Modifier.flexGrow(1).gap(6.px),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(
                        teamB?.name ?: t.profileRecentMatchesTeamB,
                        modifier = Modifier.fontSize(13.px).fontWeight(500)
                            .color(com.arena_web.ui.theme.ThemeVars.Text1).flexGrow(1)
                            .styleModifier { property("text-align", "right") }
                    )
                    if (teamB?.imageUrl != null) {
                        Img(
                            src = teamB.imageUrl!!,
                            alt = teamB.name,
                            attrs = Modifier.size(24.px).borderRadius(12.px).toAttrs()
                        )
                    } else {
                        Box(
                            modifier = Modifier.size(24.px).borderRadius(12.px)
                                .backgroundColor(Color(teamB?.color ?: "var(--l-s3, #03051F)")),
                            contentAlignment = Alignment.Center
                        ) {
                            Icon(src = AppIcons.TeamPeople, modifier = Modifier.size(14.px))
                        }
                    }
                }
            }
            // Meta: date · participants/max · location (· cost)
            Row(
                modifier = Modifier.fillMaxWidth().gap(6.px),
                verticalAlignment = Alignment.CenterVertically
            ) {
                val parts = buildList {
                    add(date)
                    add(t.profileRecentMatchesPeopleCount(match.participantsCount))
                    if (locationLabel.isNotBlank()) add(locationLabel)
                }
                SpanText(
                    parts.joinToString(" · "),
                    modifier = Modifier.fontSize(11.px).fontWeight(AppTypography.Regular)
                        .color(com.arena_web.ui.theme.ThemeVars.Text3).flexGrow(1)
                        .styleModifier {
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                            property("white-space", "nowrap")
                        }
                )
            }
        }
    } else {
        // Desktop: per Figma 7582:173680 — stadium-image card with dark overlay
        DesktopMatchCard(match = match, onClick = { ctx.router.navigateTo("${Routes.EVENTS}/${match.id}") })
    }
}

// =============================================================================
// Desktop match card — Figma "Match Scores" (7582:173680)
// =============================================================================
@Composable
private fun DesktopMatchCard(match: EventDto, onClick: () -> Unit) {
    val t = LocalAppStrings.current
    val resultText = when (match.result?.uppercase()) {
        "WIN" -> t.profileRecentMatchesWin
        "LOSS" -> t.profileRecentMatchesLoss
        "DRAW" -> t.profileRecentMatchesDraw
        else -> null
    }
    val date = formatShortDate(match.dateTime)

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .sHeight(150)
            .sBorderRadius(18)
            .overflow(Overflow.Hidden)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("position", "relative")
            }
    ) {
        // Stadium background image
        Box(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier {
                    property("position", "absolute")
                    property("inset", "0")
                    property(
                        "background-image",
                        "url('${match.imageUrl ?: AppIcons.EventFieldBackground}')"
                    )
                    property("background-size", "cover")
                    property("background-position", "center")
                }
        )
        // Gradient border ring — ButtonBorderGradientPrimary, mask-composite trick
        // so it follows the radius-18 corners.
        Box(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier {
                    property("position", "absolute")
                    property("inset", "0")
                    property("border", "0.3px solid transparent")
                    property("border-radius", s(18))
                    property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                    property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("-webkit-mask-composite", "xor")
                    property("mask-composite", "exclude")
                    property("pointer-events", "none")
                    property("z-index", "2")
                }
        )
        // Dark gradient overlay (left → right, fading out)
        Box(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier {
                    property("position", "absolute")
                    property("inset", "0")
                    property(
                        "background",
                        "linear-gradient(90deg, rgba(0, 0, 0, 0.9) 10%, " +
                            "rgba(0, 0, 0, 0.7) 38.5%, " +
                            "rgba(0, 0, 0, 0.5) 60%, " +
                            "rgba(0, 0, 0, 0.3) 75.7%, " +
                            "rgba(0, 0, 0, 0.2) 91.4%)"
                    )
                }
        )

        // Content
        Column(
            modifier = Modifier
                .fillMaxSize()
                .sPadding(12)
                .sGap(12)
                .styleModifier {
                    property("position", "relative")
                    property("justify-content", "space-between")
                }
        ) {
            // Top section: title + result + sport tag — and location/date row directly below
            Column(modifier = Modifier.fillMaxWidth().sGap(6)) {
                // Title row: Weekend Cup | Win  ......  Football | Pro+
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(
                        modifier = Modifier.sGap(18).flexGrow(1),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        SpanText(
                            match.title,
                            modifier = Modifier
                                .sFontSize(22)
                                .fontWeight(AppTypography.Medium)
                                .sLineHeight(26)
                                .sLetterSpacing(0.22)
                                .color(AppColors.Light.Background)
                        )
                        if (resultText != null) {
                            // Figma "Line 132" — 1×24 separator
                            Box(
                                modifier = Modifier
                                    .sWidth(1)
                                    .sHeight(24)
                                    .styleModifier { property("background", "rgba(255, 255, 255, 0.3)") }
                            )
                            ResultPill(resultText)
                        }
                    }
                    SportTagPill(
                        sport = (match.customSportName ?: match.sportName).ifBlank { t.profileRecentMatchesSportFallback },
                        tier = if (match.isPro) t.profileRecentMatchesProPlus else null
                    )
                }

                // Location row (city if present, else address)
                Row(
                    modifier = Modifier.fillMaxWidth().sGap(4),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    // theme-invariant: address row sits on the photo card
                    Icon(src = AppIcons.Location, modifier = Modifier.sSize(16))
                    SpanText(
                        (match.city?.takeIf { it.isNotBlank() } ?: match.address).ifBlank { "—" },
                        modifier = Modifier
                            .sFontSize(16)
                            .fontWeight(AppTypography.Regular)
                            .sLineHeight(24)
                            .sLetterSpacing(0.16)
                            .color(org.jetbrains.compose.web.css.Color("#BED6F5"))
                    )
                }
            }

            // Bottom row: format / date / members (gap-18, 14px Light)
            Row(
                modifier = Modifier.sGap(18),
                verticalAlignment = Alignment.CenterVertically
            ) {
                match.format?.let { f ->
                    MatchMetaItem(
                        icon = AppIcons.FootballField,
                        iconWidth = 27,
                        iconHeight = 14,
                        text = t.profileRecentMatchesFormat(f)
                    )
                }
                MatchMetaItem(
                    icon = AppIcons.Calendar,
                    iconSize = 16,
                    text = date
                )
                MatchMetaItem(
                    icon = AppIcons.People,
                    iconSize = 16,
                    text = t.profileRecentMatchesMembers(match.participantsCount)
                )
            }
        }
    }
}

@Composable
private fun ResultPill(text: String) {
    Box(
        modifier = Modifier
            .sHeight(32)
            .sBorderRadius(100)
            .styleModifier {
                property("padding", "${s(10)} ${s(16)}")
                property(
                    "background",
                    "linear-gradient(rgba(11, 22, 46, 0.8), rgba(11, 22, 46, 0.8)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
                property("border", "0.3px solid transparent")
            },
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            text,
            modifier = Modifier
                .sFontSize(18)
                .fontWeight(AppTypography.SemiBold)
                .sLineHeight(22)
                .sLetterSpacing(0.09)
                .color(LocalSportColors.current.metricsPrimary)
        )
    }
}

@Composable
private fun SportTagPill(sport: String, tier: String?) {
    Row(
        modifier = Modifier
            .sHeight(32)
            .sBorderRadius(100)
            .sGap(8)
            .styleModifier {
                property("padding", "${s(10)} ${s(22)}")
                property(
                    "background",
                    "linear-gradient(rgba(11, 22, 46, 0.8), rgba(11, 22, 46, 0.8)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
                property("border", "0.3px solid transparent")
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        SpanText(
            sport,
            modifier = Modifier
                .sFontSize(16)
                .fontWeight(AppTypography.Medium)
                .sLineHeight(18)
                .sLetterSpacing(0.032)
                .color(org.jetbrains.compose.web.css.Color("#BED6F5")) // theme-invariant: dark pill over photo
        )
        if (tier != null) {
            Box(
                modifier = Modifier
                    .sWidth(1)
                    .sHeight(17)
                    .styleModifier { property("background", "rgba(255, 255, 255, 0.3)") }
            )
            SpanText(
                tier,
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.SemiBold)
                    .sLineHeight(18)
                    .sLetterSpacing(0.032)
                    .color(AppColors.Light.Background)
            )
        }
    }
}

@Composable
private fun MatchMetaItem(
    icon: String,
    text: String,
    iconSize: Number = 16,
    iconWidth: Number? = null,
    iconHeight: Number? = null,
) {
    Row(
        modifier = Modifier.sGap(8),
        verticalAlignment = Alignment.CenterVertically
    ) {
        val iconMod = if (iconWidth != null && iconHeight != null) {
            Modifier.sWidth(iconWidth).sHeight(iconHeight)
        } else {
            Modifier.sSize(iconSize)
        }
        Icon(src = icon, modifier = iconMod) // theme-invariant: over photo row
        SpanText(
            text,
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(AppTypography.Light)
                .sLineHeight(18)
                .sLetterSpacing(0.028)
                .color(org.jetbrains.compose.web.css.Color("#BED6F5"))
                .styleModifier { property("white-space", "nowrap") }
        )
    }
}

private fun formatShortDate(iso: String): String = try {
    val d = kotlin.js.Date(iso)
    val months = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
    "${d.getDate()} ${months[d.getMonth()]}"
} catch (_: Throwable) { iso }

// Figma 9414:351258 — empty state for the Recent events card.
@Composable
private fun NoEventsEmptyState() {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier.fillMaxWidth().sGap(28),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        TintedGlyph(src = AppIcons.EmptyCalendarLarge, sizePx = 91)
        Column(
            modifier = Modifier.sGap(12),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            SpanText(
                t.profileRecentMatchesEmptyTitle,
                modifier = Modifier.h2().color(com.arena_web.ui.theme.ThemeVars.Text2)
            )
            SpanText(
                t.profileRecentMatchesEmptyHint,
                modifier = Modifier.bodyXlMedium().color(com.arena_web.ui.theme.ThemeVars.Text2)
            )
        }
    }
}
